/**
 * Navigation & Routing Constants
 * ============================================================================
 *
 * Purpose:
 * --------
 * Centralizes all route paths, permissions, and navigation-related
 * configuration for the application.
 *
 * This file defines:
 * - All application route paths (public and protected)
 * - Route permission mappings by user role
 * - Layout types for different route groups
 * - Navigation metadata and configuration
 *
 * Design Principles:
 * ------------------
 * - Route paths are treated as PUBLIC CONTRACTS.
 * - Values should be stable and backward-compatible.
 * - Components must not hardcode route paths.
 * - Use these constants for type-safe navigation.
 *
 * Non-goals:
 * ----------
 * - Route component definitions (handled by route config)
 * - Navigation logic (handled by navigation service)
 * - Authentication logic (handled by auth service)
 *
 * Any changes here affect navigation behavior across the entire
 * application and should be reviewed carefully.
 * ============================================================================
 */

import type { UserRole } from './types';

/**
 * Application route paths
 *
 * Centralized definition of all routes in the application.
 * Use these constants instead of hardcoding paths.
 */
export const ROUTE_PATHS = {
  // Public routes
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  VERIFY_OTP: '/verify-otp',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  ACCEPT_INVITATION: '/accept-invitation',

  // Protected - Dashboard
  OVERVIEW: '/overview',
  DASHBOARD: '/dashboard',
  NOTIFICATIONS: '/notifications',

  // Protected - Cover Notes
  COVER_NOTES: '/cover-notes',
  COVER_NOTE_CREATE: '/cover-notes/create',
  COVER_NOTE_DETAIL: '/cover-notes/:id',
  COVER_NOTE_EDIT: '/cover-notes/:id/edit',

  // Protected - Policies
  POLICIES: '/policies',
  POLICY_CREATE: '/policies/create',
  POLICY_DETAIL: '/policies/:id',
  POLICY_EDIT: '/policies/:id/edit',

  // Protected - Claims
  CLAIMS: '/claims',
  CLAIM_CREATE: '/claims/create',
  CLAIM_DETAIL: '/claims/:id',
  CLAIM_EDIT: '/claims/:id/edit',

  // Protected - Customers
  CUSTOMERS: '/customers',
  CUSTOMER_CREATE: '/customers/create',
  CUSTOMER_DETAIL: '/customers/:id',

  // Protected - Products
  PRODUCTS: '/products',
  PRODUCT_CREATE: '/products/create',
  PRODUCT_LEARN_MORE: '/products/learn-more',
  TIRA_CATALOG: '/products/tira-catalog',
  PRODUCT_DETAIL: '/products/:id',
  PRODUCT_EDIT: '/products/:id/edit',

  // Protected - Agents
  AGENTS: '/agents',
  AGENT_CREATE: '/agents/create',
  AGENT_DETAIL: '/agents/:id',
  AGENT_EDIT: '/agents/:id/edit',

  // Protected - Relationships (broker <-> insurer)
  RELATIONSHIPS: '/relationships',
  RELATIONSHIP_DETAIL: '/relationships/:id',

  // Protected - Branches
  BRANCHES: '/branches',
  BRANCH_CREATE: '/branches/create',
  BRANCH_DETAIL: '/branches/:id',

  // Protected - Payments
  PAYMENTS: '/payments',
  PAYMENT_CREATE: '/payments/create',
  PAYMENT_DETAIL: '/payments/:id',

  // Protected - Users (Admin)
  USERS: '/users',
  USER_CREATE: '/users/create',
  USER_DETAIL: '/users/:id',
  USER_EDIT: '/users/:id/edit',

  // Protected - Compliance (document verification queue)
  COMPLIANCE_DOCUMENTS: '/compliance/documents',

  // Protected - Reconciliation (bank statement ingest + match)
  RECONCILIATION: '/finance/reconciliation',
  RECONCILIATION_DETAIL: '/finance/reconciliation/:id',

  // Protected - Settings & Profile
  SETTINGS: '/settings',
  PROFILE: '/profile',
  HELP: '/help',

  // Error pages
  NOT_FOUND: '/404',
  UNAUTHORIZED: '/403',
  SERVER_ERROR: '/500',

  // Admin portal
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_BUSINESSES: '/admin/businesses',
  ADMIN_BUSINESS_DETAIL: '/admin/businesses/:id',
  ADMIN_SUPPORT: '/admin/support',
  ADMIN_SUPPORT_DETAIL: '/admin/support/:id',
  ADMIN_REGULATORY: '/admin/regulatory',
  ADMIN_REGULATORY_DETAIL: '/admin/regulatory/:id',
  ADMIN_NOTIFICATIONS: '/admin/notifications',
  ADMIN_NOTIFICATION_DETAIL: '/admin/notifications/:id',
  ADMIN_NOTIFICATION_NEW: '/admin/notifications/new',
  ADMIN_ANALYTICS: '/admin/analytics',
  ADMIN_BILLING: '/admin/billing',
  ADMIN_TRANSACTIONS:       '/admin/transactions',
  ADMIN_TRANSACTION_DETAIL: '/admin/transactions/:id',
  ADMIN_INVOICES: '/admin/invoices',
  ADMIN_INVOICE_DETAIL: '/admin/invoices/:id',
  ADMIN_PAYMENTS: '/admin/payments',
  ADMIN_PAYMENT_DETAIL: '/admin/payments/:id',
  ADMIN_TEAM: '/admin/team',
  ADMIN_TEAM_MEMBER_DETAIL: '/admin/team/:id',
  ADMIN_AUDIT_LOG: '/admin/audit-log',
  ADMIN_CONFIG: '/admin/config',
  ADMIN_SETTINGS: '/admin/settings',
  ADMIN_HELP: '/admin/help',
} as const;

/**
 * Layout types for route rendering
 *
 * Determines which layout wrapper to use for route groups.
 */
export const LAYOUT_TYPES = {
  NONE: 'none', // No layout (e.g., 404 page)
  AUTH: 'auth', // Auth pages (login, register)
  DASHBOARD: 'dashboard', // Main app layout with sidebar
  FULLSCREEN: 'fullscreen', // Full screen (e.g., presentations)
  PUBLIC: 'public', // Public website layout
  ADMIN: 'admin', // Admin portal layout
} as const;

/**
 * User roles for permission checks
 *
 * Maps to backend role system.
 */
export const USER_ROLES = {
  SUPER_ADMIN: 'BUSINESS_ADMIN', // this can be changed to 'SUPER_ADMIN' if needed
  ADMIN: 'ADMIN',
  MANAGER: 'MANAGER',
  AGENT: 'AGENT',
  CUSTOMER: 'CUSTOMER',
  GUEST: 'GUEST',
} as const;

/**
 * Route permissions by user role
 *
 * Defines which roles can access which routes.
 * '*' means access to all routes.
 */
export const ROUTE_PERMISSIONS: Record<UserRole, string[] | '*'> = {
  [USER_ROLES.SUPER_ADMIN]: '*', // Full access
  [USER_ROLES.ADMIN]: '*', // Full access
  [USER_ROLES.MANAGER]: [
    ROUTE_PATHS.OVERVIEW,
    ROUTE_PATHS.POLICIES,
    ROUTE_PATHS.POLICY_DETAIL,
    ROUTE_PATHS.CLAIMS,
    ROUTE_PATHS.CLAIM_DETAIL,
    ROUTE_PATHS.CUSTOMERS,
    ROUTE_PATHS.CUSTOMER_DETAIL,
    ROUTE_PATHS.PRODUCTS,
    ROUTE_PATHS.SETTINGS,
    ROUTE_PATHS.PROFILE,
    ROUTE_PATHS.HELP,
  ],
  [USER_ROLES.AGENT]: [
    ROUTE_PATHS.OVERVIEW,
    ROUTE_PATHS.POLICIES,
    ROUTE_PATHS.POLICY_CREATE,
    ROUTE_PATHS.POLICY_DETAIL,
    ROUTE_PATHS.CLAIMS,
    ROUTE_PATHS.CLAIM_CREATE,
    ROUTE_PATHS.CLAIM_DETAIL,
    ROUTE_PATHS.CUSTOMERS,
    ROUTE_PATHS.CUSTOMER_CREATE,
    ROUTE_PATHS.CUSTOMER_DETAIL,
    ROUTE_PATHS.PRODUCTS,
    ROUTE_PATHS.PROFILE,
    ROUTE_PATHS.HELP,
  ],
  [USER_ROLES.CUSTOMER]: [
    ROUTE_PATHS.OVERVIEW,
    ROUTE_PATHS.POLICIES,
    ROUTE_PATHS.POLICY_DETAIL,
    ROUTE_PATHS.CLAIMS,
    ROUTE_PATHS.CLAIM_CREATE,
    ROUTE_PATHS.CLAIM_DETAIL,
    ROUTE_PATHS.PROFILE,
    ROUTE_PATHS.HELP,
  ],
  [USER_ROLES.GUEST]: [], // No access to protected routes
};

/**
 * Public routes that don't require authentication
 */
export const PUBLIC_ROUTES = [
  ROUTE_PATHS.HOME,
  ROUTE_PATHS.LOGIN,
  ROUTE_PATHS.REGISTER,
  ROUTE_PATHS.VERIFY_OTP,
  ROUTE_PATHS.FORGOT_PASSWORD,
  ROUTE_PATHS.RESET_PASSWORD,
  ROUTE_PATHS.ACCEPT_INVITATION,
  ROUTE_PATHS.NOT_FOUND,
  ROUTE_PATHS.UNAUTHORIZED,
  ROUTE_PATHS.SERVER_ERROR,
] as const;

/**
 * Routes that should redirect to dashboard if user is already authenticated
 */
export const AUTH_REDIRECT_ROUTES = [
  ROUTE_PATHS.LOGIN,
  ROUTE_PATHS.REGISTER,
  ROUTE_PATHS.HOME,
] as const;

/**
 * Default redirect paths based on user role
 *
 * Where to send user after login based on their role.
 */
export const DEFAULT_REDIRECT_BY_ROLE: Record<UserRole, string> = {
  [USER_ROLES.SUPER_ADMIN]: ROUTE_PATHS.DASHBOARD,
  [USER_ROLES.ADMIN]: ROUTE_PATHS.DASHBOARD,
  [USER_ROLES.MANAGER]: ROUTE_PATHS.OVERVIEW,
  [USER_ROLES.AGENT]: ROUTE_PATHS.POLICIES,
  [USER_ROLES.CUSTOMER]: ROUTE_PATHS.OVERVIEW,
  [USER_ROLES.GUEST]: ROUTE_PATHS.LOGIN,
};

/**
 * Navigation storage keys
 *
 * Keys for persisting navigation state to localStorage.
 */
export const NAVIGATION_STORAGE_KEYS = {
  LAST_VISITED_ROUTE: 'bimanova_last_visited_route',
  REDIRECT_AFTER_LOGIN: 'bimanova_redirect_after_login',
  NAVIGATION_HISTORY: 'bimanova_navigation_history',
} as const;
